import { useRect } from '@dx-ui/utilities-use-rect';
import cx from 'classnames';
import * as React from 'react';
import { usePopup } from './popup.context';
import { getPopupStyles } from './popup.utils';
import { useForkedRef } from '@dx-ui/utilities-use-forked-ref';
import { useBrandConfig } from '@dx-ui/global-ui-prism';

export type Side = 'top' | 'right' | 'bottom' | 'left';

export type PopupContentPropsBase = {
  popupStyles?: React.CSSProperties;
  side?: Side;
} & React.ComponentProps<'div'>;

/** Used in conjunction with [Popup](?path=/docs/library-components-popup--docs), PopupContent is a component that wraps the content of a popup.
 */
const PopupContent = ({
  ref: forwardedRef,
  children,
  className,
  style,
  popupStyles = {},
  side = 'bottom',
  ...rest
}: PopupContentPropsBase) => {
  const { triggerRef, triggerClickedRef, popoverRef, popupId, isOpen, setOpen, closePopup } =
    usePopup();
  const ref = useForkedRef(forwardedRef, popoverRef);

  React.useEffect(() => {
    const popoverRefCurrent = popoverRef?.current;
    const onMouseDown = (e: MouseEvent) => {
      if (triggerClickedRef.current) {
        triggerClickedRef.current = false;
      } else if (isOpen && popoverRefCurrent && !popoverRefCurrent.contains(e.target as Element)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (isOpen) {
        //parent elements such as <Dialog /> may have event listeners for keydown. Make sure we dont bubble the event back up
        e.stopPropagation();
        switch (e.key) {
          case 'Enter':
          case ' ':
            if (triggerClickedRef.current) {
              triggerClickedRef.current = false;
            } else if (
              isOpen &&
              popoverRefCurrent &&
              !popoverRefCurrent.contains(e.target as Element)
            ) {
              setOpen(false);
            }
            break;
          case 'Escape':
            closePopup();
            break;
          case 'Tab':
            requestAnimationFrame(() => {
              if (popoverRefCurrent && !popoverRefCurrent.contains(document.activeElement)) {
                setOpen(false);
              }
            });
            break;
          default:
            break;
        }
      }
    };
    if (popoverRefCurrent) {
      document.addEventListener('mousedown', onMouseDown);
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      if (popoverRefCurrent) {
        document.removeEventListener('mousedown', onMouseDown);
        document.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [triggerClickedRef, triggerRef, closePopup, isOpen, popoverRef, setOpen]);

  const popoverRect = useRect({ ref: popoverRef });
  const targetRect = useRect({ ref: triggerRef });
  const styles = getPopupStyles(targetRect, popoverRect, side);

  const brandConfig = useBrandConfig();
  const isSurfaceEnabled = brandConfig.surfaceConfig.isEnabled;

  return (
    <div
      id={popupId}
      data-testid="popup-content"
      ref={ref}
      className={cx(
        'fixed z-50 mt-1 rounded border border-solid p-2 shadow-lg',
        {
          block: isOpen,
          hidden: !isOpen,
          'border-surface bg-surface': isSurfaceEnabled,
          'border-border bg-bg': !isSurfaceEnabled,
        },
        className
      )}
      style={{ ...style, ...styles, ...popupStyles }}
      {...rest}
    >
      {children}
    </div>
  );
};

export { PopupContent };
export default PopupContent;
